import { salesMailto } from "../config/public";
import {
  publicCrmAmountLabel,
  publicCrmPrice,
  type PublicCrmPricing,
} from "./publicCrmPricing";

interface MarketingPricingSectionProps {
  pricing: PublicCrmPricing | null;
  loading: boolean;
  failed: boolean;
}

export function MarketingPricingSection({
  pricing,
  loading,
  failed,
}: MarketingPricingSectionProps) {
  const offer = pricing?.plan.founding_offer;
  const foundingPrice = Boolean(offer?.available);
  const activePrices = foundingPrice ? offer?.prices || [] : pricing?.plan.prices || [];
  const monthly = publicCrmPrice(activePrices, "MONTHLY");
  const annual = publicCrmPrice(activePrices, "ANNUAL");
  const listMonthly = publicCrmPrice(pricing?.plan.prices || [], "MONTHLY");
  const additionalUserMonthly = publicCrmPrice(
    pricing?.plan.additional_user_prices || [],
    "MONTHLY",
  );
  const seats = pricing?.plan.base_included_seats;
  const introductoryOffer = !foundingPrice && pricing?.plan.code === "GROWING"
    && seats === 1 && monthly?.currency === "INR"
    && [69900, 99900].includes(monthly.amount_minor_units);
  const introductoryMonthly = introductoryOffer && monthly ? { ...monthly, amount_minor_units: 69900 } : monthly;
  const integrations = pricing?.plan.included_integrations || [];
  const integrationNames = integrations.map((integration) => integration.name);
  const storefront = pricing?.plan.addons?.find(
    (addon) => addon.code === "LEADCAT_COMMERCE_STOREFRONT",
  );
  const storefrontMonthly = publicCrmPrice(storefront?.prices || [], "MONTHLY");
  const bundle = pricing?.plans?.find((plan) => plan.code === "GROW_PLUS");
  const bundleMonthly = publicCrmPrice(bundle?.prices || [], "MONTHLY");

  return (
    <section className="pm-pricing" id="pricing" aria-labelledby="pm-pricing-title">
      <header className="pm-section-heading pm-pricing-heading">
        <span>LeadCat CRM Pricing</span>
        <h2 id="pm-pricing-title">Simple plans for managing social enquiries</h2>
        <p>Start with the full LeadCat CRM workflow—channels, ownership, follow-ups and conversion visibility in one workspace.</p>
      </header>

      {loading ? (
        <div className="pm-pricing-loading" role="status" aria-label="Loading current LeadCat CRM pricing">
          <span /><span /><span />
        </div>
      ) : failed || !pricing || !monthly ? (
        <article className="pm-pricing-unavailable">
          <span>Current pricing</span>
          <h3>Let us confirm the right LeadCat CRM offer for you.</h3>
          <p>Pricing could not be loaded right now. Contact our team for the current plan and activation terms.</p>
          <a className="pm-button pm-button-primary" href={salesMailto("LeadCat CRM pricing")}>
            Ask about pricing <span aria-hidden="true">→</span>
          </a>
        </article>
      ) : (
        <div className="pm-pricing-layout">
          <article className="pm-pricing-card">
            <div className="pm-pricing-card-head">
              <div>
                <span className="pm-pricing-badge">{introductoryOffer ? "3-month introductory offer" : foundingPrice ? "Founding price" : "LeadCat CRM"}</span>
                <h3>{pricing.plan.name}</h3>
                <p>Complete social CRM for a team that wants every enquiry to have an owner and a next step.</p>
              </div>
              <div className="pm-price-block">
                {foundingPrice && listMonthly ? <del>{publicCrmAmountLabel(listMonthly)}</del> : null}
                <div><strong>{publicCrmAmountLabel(introductoryMonthly)}</strong><span>/ month</span></div>
                {introductoryOffer ? <>
                  <small>For the first 3 months</small>
                  <p className="pm-price-renewal">₹999 / month from month 4</p>
                </> : annual ? <small>or {publicCrmAmountLabel(annual)} / year</small> : null}
              </div>
            </div>

            <div className="pm-pricing-features" aria-label="Social Media CRM plan includes">
              <span><i aria-hidden="true">✓</i>{seats} user{seats === 1 ? "" : "s"} included</span>
              {additionalUserMonthly ? <span><i aria-hidden="true">✓</i>Additional users {publicCrmAmountLabel(additionalUserMonthly)} / month each</span> : null}
              <span><i aria-hidden="true">✓</i>{integrationNames.length ? integrationNames.join(", ") : "Instagram, Facebook, WhatsApp"} integrations</span>
              <span><i aria-hidden="true">✓</i>Lead assignment and follow-up tracking</span>
              <span><i aria-hidden="true">✓</i>Sales pipeline and revenue visibility</span>
              <span><i aria-hidden="true">✓</i>Quotations and payment tracking</span>
              <span><i aria-hidden="true">✓</i>Reports and team visibility</span>
            </div>

            <div className="pm-pricing-actions">
              <a className="pm-button pm-button-primary" href="/react/signup">
                {foundingPrice ? "Get Founding Price" : "Get Started"} <span aria-hidden="true">→</span>
              </a>
              <small>{introductoryOffer ? "For 1 user with an eligible promo code on monthly billing. ₹699/month for 3 months, then ₹999/month. Review the offer terms before payment." : "Review the current billing and activation terms before payment."}</small>
            </div>
          </article>

          <aside className="pm-pricing-side" aria-label="LeadCat CRM add-ons and pricing questions">
            {bundle && bundleMonthly ? <article className="pm-pricing-card pm-pricing-bundle" aria-label="Grow Plus plan">
              <span className="pm-pricing-badge">CRM + Storefront</span>
              <h3>{bundle.name}</h3>
              <div className="pm-price-block"><div><strong>{publicCrmAmountLabel(bundleMonthly)}</strong><span>/ month</span></div></div>
              <p>{bundle.base_included_seats} users included. All Grow features, Storefront, custom workflows, quotations and revenue tracking.</p>
              <a className="pm-button pm-button-primary" href="/react/signup">Choose Grow Plus</a>
            </article> : null}
            {storefront && storefrontMonthly ? <article className="pm-pricing-addon" aria-label="Optional Storefront add-on">
              <span>Optional add-on</span>
              <h3>{storefront.name}</h3>
              <div><strong>{publicCrmAmountLabel(storefrontMonthly)}</strong><small>/ month</small></div>
              <p>{storefront.description} Activation and billing are separate from the Grow plan.</p>
              <a className="pm-button pm-button-secondary pm-button-compact" href="mailto:director@staminal.in?subject=LeadCat%20Storefront%20add-on">
                Ask about Storefront
              </a>
            </article> : null}
            <div className="pm-pricing-faq">
              <h3>Good to know</h3>
              <details>
                <summary>What does the plan include?</summary>
                <p>The three customer channels, lead pipeline, assignment, follow-ups, quotations, revenue tracking and reporting shown above.</p>
              </details>
              <details>
                <summary>Is Storefront included?</summary>
                <p>{bundle ? "Storefront is included in Grow Plus. On Grow, it is an optional ₹499/month add-on with separate assisted activation." : "No. LeadCat Storefront is an optional ₹499/month add-on with separate assisted activation."}</p>
              </details>
              <details>
                <summary>Are provider charges included?</summary>
                <p>Charges billed separately by Meta, Razorpay or another connected provider are not included when applicable.</p>
              </details>
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}
